import { ConfigApi, createApiRef, FetchApi } from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/plugin-catalog-react';

import {
  Tenant,
  UpdateTenant,
  OwnerObject,
  Application,
  Cluster,
} from './types';
import {
  deserializeTenant,
  deserializeApplication,
  deserializeCluster,
  serializeTenant,
} from './utils/jsonserializer';

export const kaapApiRef = createApiRef<KaapApi>({
  id: 'plugin.kaap',
});

export interface KaapApi {
  getTenants(token: string): Promise<Tenant[]>;
  getTenant(tenantName: string, token: string): Promise<Tenant | undefined>;
  createTenant(tenant: Tenant, token: string): Promise<any>;
  updateTenant(tenant: UpdateTenant, token: string): Promise<any>;
  deleteTenant(tenant: Tenant, token: string): Promise<any>;
  getClusters(token: string): Promise<Cluster[]>;
  getApplications(token: string): Promise<Application[]>;
  getSupervisoryOrgFromCatalog(
    supervisoryOrgId: string,
    catalogApi: CatalogApi,
  ): Promise<OwnerObject | null>;
}

export class KaapApiClient implements KaapApi {
  private readonly fetchApi: FetchApi;
  private readonly baseUrl: string;

  constructor(fetchApi: FetchApi, configApi: ConfigApi) {
    this.fetchApi = fetchApi;
    this.baseUrl = `${configApi.getString('kaap.apiUrl')}/v1`;
  }

  async getTenants(token: string): Promise<Tenant[]> {
    const url = `${this.baseUrl}/tenants`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const { tenants: rawTenants } = await response.json();
    return rawTenants.map(deserializeTenant);
  }

  async getTenant(
    tenantName: string,
    token: string,
  ): Promise<Tenant | undefined> {
    const url = `${this.baseUrl}/tenants/${tenantName}`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const tenant = await response.json();
    if (!tenant || tenant?.detail) {
      return undefined;
    }

    return deserializeTenant(tenant);
  }

  async getClusters(token: string): Promise<Cluster[]> {
    const url = `${this.baseUrl}/clusters`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const { clusters } = await response.json();
    return clusters.map(deserializeCluster);
  }

  async createTenant(tenant: Tenant, token: string): Promise<any> {
    const url = `${this.baseUrl}/tenants`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      body: JSON.stringify(serializeTenant(tenant)),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return { status: response.status, body: await response.json() };
  }

  async updateTenant(tenant: Tenant, token: string): Promise<any> {
    const url = `${this.baseUrl}/tenants/${tenant.name}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'PUT',
      body: JSON.stringify(serializeTenant(tenant as UpdateTenant)),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return { status: response.status, body: await response.json() };
  }

  async deleteTenant(tenant: Tenant, token: string): Promise<any> {
    const url = `${this.baseUrl}/tenants/${tenant.name}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }

  async getApplications(token: string): Promise<Application[]> {
    const url = `${this.baseUrl}/applications`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const { applications } = await response.json();
    return applications.map(deserializeApplication);
  }

  async getSupervisoryOrgFromCatalog(
    supervisoryOrgId: string,
    catalogApi: CatalogApi,
  ): Promise<OwnerObject | null> {
    const result = await catalogApi.getEntities({
      filter: {
        kind: 'Group',
        'metadata.name': supervisoryOrgId.toLocaleLowerCase(),
      },
    });

    if (result.items.length > 0) {
      const groupEntity = result.items[0];
      const sorg: OwnerObject = {
        name: groupEntity.metadata.title || groupEntity.metadata.name || '',
        id: supervisoryOrgId,
      };

      return sorg;
    }

    return null;
  }
}
